import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActiveCrawlJobs, CrawlStatus } from '../services/crawlStatusService';

const CrawlProgressIndicator: React.FC = () => {
    const navigate = useNavigate();
    const [activeCrawls, setActiveCrawls] = useState<CrawlStatus[]>([]);
    const [isVisible, setIsVisible] = useState(false);
    const [isFading, setIsFading] = useState(false);

    const getStageDescriptor = (value?: CrawlStatus['status']) => {
        switch (value) {
            case 'initializing':
                return { label: 'Preparing', terminal: false };
            case 'in_progress':
            case 'crawling':
            case 'scraping':
                return { label: 'Crawling', terminal: false };
            case 'processing':
                return { label: 'Processing', terminal: false };
            case 'embedding':
            case 'stopped':
                return { label: 'AI Training', terminal: false };
            case 'completed':
                return { label: 'Completed', terminal: true };
            case 'failed':
                return { label: 'Failed', terminal: true };
            case 'aborted':
                return { label: 'Aborted', terminal: true };
            default:
                return { label: 'In Progress', terminal: false };
        }
    };

    useEffect(() => {
        // Check for active crawl jobs every second
        const interval = setInterval(() => {
            const jobs = getActiveCrawlJobs();
            // Only show crawls that are actively running
            const activeCrawlsOnly = jobs.filter(job =>
                job.status === 'initializing' ||
                job.status === 'in_progress' ||
                job.status === 'crawling' ||  // Support async webhook crawls
                job.status === 'scraping' ||  // Support batch scrape mode
                job.status === 'processing' ||  // Support processing phase
                job.status === 'embedding' ||
                job.status === 'stopped'  // Show while embedding after stop
            );
            
            // Handle visibility transitions
            if (activeCrawlsOnly.length > 0 && !isVisible) {
                setIsVisible(true);
                setIsFading(false);
            } else if (activeCrawlsOnly.length === 0 && isVisible) {
                setIsFading(true);
                // Wait for fade animation to complete before hiding
                setTimeout(() => {
                    setIsVisible(false);
                    setIsFading(false);
                }, 300);
            }
            
            setActiveCrawls(activeCrawlsOnly);
        }, 1000);

        return () => clearInterval(interval);
    }, [isVisible]);

    if (!isVisible && activeCrawls.length === 0) return null;

    // Get the most recent active crawl
    const currentCrawl = activeCrawls[0];
    if (!currentCrawl) return null;
    
    const pagesCrawled = currentCrawl.pages_crawled ?? 0;
    const totalPages = currentCrawl.estimated_total_pages ?? currentCrawl.total_pages ??
        (currentCrawl.pages_in_queue !== undefined ? pagesCrawled + (currentCrawl.pages_in_queue ?? 0) : pagesCrawled);
    const pagesRemaining = currentCrawl.pages_remaining ?? currentCrawl.pages_in_queue ??
        Math.max((typeof totalPages === 'number' ? totalPages : pagesCrawled) - pagesCrawled, 0);

    // Calculate progress based on chunks if available, otherwise use progress_percentage
    let progressValue: number | undefined;

    const chunksTotal = currentCrawl.chunks_total;
    const chunksCompleted = currentCrawl.chunks_completed;
    const chunkIndex = currentCrawl.chunk_index;

    // If we have chunk data, calculate chunk-based progress
    if (chunksTotal && typeof chunksTotal === 'number' && chunksTotal > 0) {
        const completed = chunksCompleted ?? 0;
        progressValue = Math.round((completed / chunksTotal) * 100);
    } else {
        // Fall back to progress_percentage from backend
        progressValue = currentCrawl.progress_percentage;

        // Only use embedding_progress if no chunk data and no progress_percentage
        if ((progressValue === undefined || progressValue === null) &&
            (currentCrawl.status === 'embedding' || currentCrawl.status === 'stopped') &&
            currentCrawl.embedding_progress !== undefined) {
            progressValue = currentCrawl.embedding_progress;
        }

        // Final fallback: calculate from pages
        if (progressValue === undefined || progressValue === null) {
            if (typeof totalPages === 'number' && totalPages > 0) {
                progressValue = Math.round((pagesCrawled / totalPages) * 100);
            } else {
                progressValue = 0;
            }
        }
    }

    const progress = Math.max(0, Math.min(100, progressValue));

    const stageDescriptor = getStageDescriptor(currentCrawl.status);
    const chunkLabel = currentCrawl.chunk_label || (currentCrawl.chunk_index && currentCrawl.chunks_total
        ? `${currentCrawl.chunk_index}/${currentCrawl.chunks_total}`
        : undefined);
    const tooltipParts = [
        stageDescriptor.label,
        `Pages: ${pagesCrawled}${typeof totalPages === 'number' && totalPages > 0 ? `/${totalPages}` : ''}`,
        pagesRemaining > 0 ? `Remaining: ${pagesRemaining}` : undefined,
        chunkLabel ? `Chunk ${chunkLabel}` : undefined,
        currentCrawl.message
    ].filter(Boolean);
    const tooltip = tooltipParts.join(' • ');

    const handleClick = () => {
        const clientId = currentCrawl.clientId;
        
        // Navigate to unified crawl details page for all users
        const clientIdToUse = localStorage.getItem('client_id') || clientId;
        navigate(`/crawl-details/${clientIdToUse}`);
    };


    // Circular progress SVG
    const size = 60;
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset = circumference - (Math.max(0, Math.min(100, progress)) / 100) * circumference;

    return (
        <div 
            className={`fixed bottom-6 right-6 z-50 cursor-pointer hover:scale-110 transition-all duration-300 ${
                isFading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
            }`}
            onClick={handleClick}
            title={tooltip || 'Crawl in progress'}
        >
            <div className="relative">
                {/* Background circle */}
                <svg width={size} height={size} className="transform -rotate-90">
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke="#e5e7eb"
                        strokeWidth={strokeWidth}
                        fill="white"
                    />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke={currentCrawl.status === 'failed' ? '#ef4444' : '#3b82f6'}
                        strokeWidth={strokeWidth}
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        className="transition-all duration-300"
                    />
                </svg>
                
                {/* Center content */}
                <div className="absolute inset-0 flex items-center justify-center">
                    {currentCrawl.status === 'completed' ? (
                        <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    ) : currentCrawl.status === 'failed' ? (
                        <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <span className="text-xs font-semibold text-gray-700">{progress}%</span>
                    )}
                </div>
                
                {/* Pulse animation for active crawl */}
                {['in_progress', 'crawling', 'scraping', 'processing', 'embedding', 'stopped'].includes(currentCrawl.status) && (
                    <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20"></div>
                )}
            </div>
            
            {/* Shadow */}
            <div className="absolute inset-0 rounded-full shadow-lg -z-10"></div>
        </div>
    );
};

export default CrawlProgressIndicator;
